import React, { useState } from 'react';
import {
    Create,
    SimpleForm,
    TextInput,
    ReferenceInput,
    ReferenceArrayInput,
    AutocompleteInput,
    AutocompleteArrayInput,
    FormDataConsumer,
    required,
    useTranslate,
    Toolbar,
    SaveButton,
    useRedirect,
    useNotify,
    useGetIdentity,
    useGetList,
} from 'react-admin';
import { Box, Typography, Card, CardContent, IconButton, CircularProgress } from '@mui/material';
import { ArrowBack, Storefront, ElectricBolt } from '@mui/icons-material';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';
import { EnrollmentCodeDialog } from './EnrollmentCodeDialog';

const KIOSK_KNOWN_FIELDS = new Set(['siteId', 'name', 'nearbyChargerIds', 'octopusDeviceId']);

const mapKioskBackendPath = (path: string): string =>
    path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');

const kioskFieldLabelKey = (field: string): string =>
    KIOSK_KNOWN_FIELDS.has(field)
        ? `resources.kiosk-devices.fields.${field}`
        : field;

const CreateToolbar = () => (
    <Toolbar
        sx={{
            backgroundColor: '#FAFAFA',
            borderTop: '1px solid #E0E0E0',
            px: { xs: 2, sm: 3 },
            py: 2,
            mt: { xs: 2, sm: 4 },
        }}
    >
        <SaveButton
            variant="contained"
            sx={{
                backgroundColor: '#00C853',
                '&:hover': { backgroundColor: '#00A843' },
            }}
        />
    </Toolbar>
);

const PageHeader = () => {
    const redirect = useRedirect();
    const translate = useTranslate();

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: { xs: 2, sm: 4 } }}>
            <IconButton
                onClick={() => redirect('/kiosk-devices')}
                size="large"
                sx={{ flexShrink: 0, backgroundColor: '#FFFFFF', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                title={translate('ra.action.back')}
            >
                <ArrowBack />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '2.125rem' }, mb: 0.5 }}>
                    {translate('resources.kiosk-devices.create.title')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {translate('resources.kiosk-devices.create.subtitle')}
                </Typography>
            </Box>
        </Box>
    );
};

export const KioskDeviceCreate = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();
    const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
    const [dialogState, setDialogState] = useState<{ open: boolean; code: string; expiresAt: string | null; id: string | null }>({
        open: false,
        code: '',
        expiresAt: null,
        id: null,
    });

    const { data: identity } = useGetIdentity();
    const actorVendorIds: string[] = ((identity as any)?.vendorIds as string[]) || [];
    const isVendorScoped = actorVendorIds.length > 0;
    const { data: scopedSites, isLoading: scopedSitesLoading } = useGetList(
        'sites',
        { pagination: { page: 1, perPage: 2 }, sort: { field: 'name', order: 'ASC' }, filter: { kioskAccessEnabled: true } },
        { enabled: isVendorScoped }
    );
    const singleScopedSiteId = isVendorScoped && scopedSites?.length === 1 ? scopedSites[0].id : undefined;
    const siteScopeResolved = !isVendorScoped || !scopedSitesLoading;

    return (
        <>
            <Create
                title={false}
                component="div"
                mutationMode="pessimistic"
                mutationOptions={{
                    onSuccess: (data: any) => {
                        setServerErrors({});
                        setDialogState({ open: true, code: data.enrollmentCode, expiresAt: data.enrollmentExpiresAt ?? null, id: data.id });
                    },
                    onError: (error: any) => {
                        const apiError = error?.body || error;
                        const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
                        const fieldErrors: Record<string, string> = {};
                        const summaryLines: string[] = [];
                        if (rawErrors) {
                            for (const [path, msgs] of Object.entries(rawErrors)) {
                                const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                                const translated = translateServerMessage(rawMsg, translate);
                                const field = mapKioskBackendPath(path);
                                fieldErrors[field] = translated;
                                const label = translate(kioskFieldLabelKey(field), { _: field });
                                summaryLines.push(`• ${label}: ${translated}`);
                            }
                        }
                        setServerErrors(fieldErrors);

                        const kioskErrorMessage = apiError?.errorCode
                            ? translate(`resources.kiosk-devices.errors.${apiError.errorCode}`, { _: '' })
                            : '';
                        const header = kioskErrorMessage || translate(
                            apiError?.errorCode ? `errors.${apiError.errorCode}` : 'validation.formHasErrors',
                            { _: apiError?.message || 'Error' }
                        );

                        if (summaryLines.length > 0) {
                            notify(`${header}\n${summaryLines.join('\n')}`, { type: 'error', multiLine: true, autoHideDuration: 10000 });
                        } else {
                            notify(header, { type: 'error', autoHideDuration: 6000 });
                        }
                    },
                }}
                sx={{ backgroundColor: 'transparent', '& .RaCreate-main': { backgroundColor: 'transparent', boxShadow: 'none' } }}
            >
                <SimpleForm
                    toolbar={<CreateToolbar />}
                    sx={{
                        '& .MuiPaper-root': { backgroundColor: 'transparent', boxShadow: 'none' },
                        '& > .MuiCardContent-root, & > div > .MuiCardContent-root': { px: { xs: 0, sm: 2 }, py: { xs: 1, sm: 2 } },
                        padding: { xs: 0, sm: 'inherit' },
                    }}
                >
                    <ServerErrorBinder errors={serverErrors} />
                    <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 10, sm: 2 }, '& .MuiFormControl-root': { width: '100%' } }}>
                        <PageHeader />

                        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <Storefront sx={{ mr: 2, color: '#00C853' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        {translate('resources.kiosk-devices.sections.basicInfo')}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'grid', gap: 2 }}>
                                    {siteScopeResolved ? (
                                        <ReferenceInput source="siteId" reference="sites" filter={{ kioskAccessEnabled: true }}>
                                            <AutocompleteInput
                                                optionText="name"
                                                label={translate('resources.kiosk-devices.fields.siteId')}
                                                validate={required()}
                                                fullWidth
                                                defaultValue={singleScopedSiteId}
                                                readOnly={Boolean(singleScopedSiteId)}
                                                helperText={translate('resources.kiosk-devices.create.siteHelperText')}
                                            />
                                        </ReferenceInput>
                                    ) : (
                                        <CircularProgress size={20} />
                                    )}

                                    <TextInput
                                        source="name"
                                        label={translate('resources.kiosk-devices.fields.name')}
                                        validate={required()}
                                        fullWidth
                                        helperText={translate('resources.kiosk-devices.create.nameHelperText')}
                                    />

                                    <TextInput
                                        source="octopusDeviceId"
                                        label={translate('resources.kiosk-devices.fields.octopusDeviceId')}
                                        fullWidth
                                        helperText={translate('resources.kiosk-devices.create.octopusDeviceIdHelperText')}
                                    />
                                </Box>
                            </CardContent>
                        </Card>

                        <Card sx={{ overflow: 'hidden' }}>
                            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <ElectricBolt sx={{ mr: 2, color: '#00C853' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        {translate('resources.kiosk-devices.sections.hardware')}
                                    </Typography>
                                </Box>

                                <FormDataConsumer<{ siteId?: string }>>
                                    {({ formData }) => (
                                        <ReferenceArrayInput source="nearbyChargerIds" reference="chargers" filter={{ siteId: formData.siteId }}>
                                            <AutocompleteArrayInput
                                                optionText="name"
                                                label={translate('resources.kiosk-devices.fields.nearbyChargerIds')}
                                                fullWidth
                                                disabled={!formData.siteId}
                                                helperText={translate('resources.kiosk-devices.create.nearbyChargersHelperText')}
                                            />
                                        </ReferenceArrayInput>
                                    )}
                                </FormDataConsumer>
                            </CardContent>
                        </Card>
                    </Box>
                </SimpleForm>
            </Create>
            <EnrollmentCodeDialog
                open={dialogState.open}
                code={dialogState.code}
                expiresAt={dialogState.expiresAt}
                onClose={() => {
                    setDialogState((s) => ({ ...s, open: false }));
                    if (dialogState.id) redirect('edit', 'kiosk-devices', dialogState.id);
                }}
            />
        </>
    );
};
